import { useEffect, useRef, useState } from 'react';
import Button from 'ui/mobile-ds/Button';
import {
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from 'ui/mobile-ds/Accordion';
import { ChevronDown, ChevronUp } from 'ui/retail-ds/icons';
import ClaimCategoryInput from '../ClaimCategoryInput';
import TreatmentDetailsInput from '../TreatmentDetailsInput';
import TreatmentStateInput from '../TreatmentStateInput';
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Layout } from '@/components/Common/Layout';
import Stepper from '@/components/Common/Stepper/Stepper';
import useClaimCreateStore, {
  ClaimCreateFormSchema,
  EClaimCreateFlowSteps,
} from '@/store/claimCreateStore';
import { CARE_TYPE_CONFIG } from '@/constants';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { InfoIcon } from 'ui/icons';
import { twConfig } from 'ui/ds/utils/getConfig';
import get from 'lodash.get';
import clsx from 'clsx';
import ErrorScreen from '@/components/Common/ErrorScreen/ErrorScreen';
import { useAppContext } from '@/context/AppContext';
import useScrollBlur from '@/hooks/useScrollBlur';
import useMobileBackPress from '@/hooks/useMobileBackPress';

const Schema = ClaimCreateFormSchema.pick({
  typeOfCare: true,
  reasonForHospitalization: true,
  treatmentStage: true,
});

enum EFieldNames {
  TYPE_OF_CARE = 'typeOfCare',
  TYPE_OF_CLAIM = 'typeOfClaim',
  TREATMENT_DETAILS = 'treatmentDetails',
  TREATMENT_STAGE = 'treatmentStage',
}

type TSchema = z.infer<typeof Schema>;

const BasicInformation = () => {
  const [openedSection, setOpenedSection] = useState<string>('');
  const [isFooterHidden, setIsFooterHidden] = useState(false);

  const setCurrentStep = useClaimCreateStore((state) => state.setCurrentStep);
  const formState = useClaimCreateStore((state) => state.formState);
  const setFormState = useClaimCreateStore((state) => state.setFormState);
  const currentFeatureCategory = useClaimCreateStore(
    (state) => state.currentFeatureCategory
  );

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  const { isWebView } = useAppContext();

  useMobileBackPress({
    onBackPress: () => {
      if (isFooterHidden) {
        inputRef?.blur();
      } else {
        setCurrentStep(EClaimCreateFlowSteps.SELECT_PATIENT);
      }
    },
    isWebView,
  });

  useScrollBlur({ inputRef });

  const {
    register,
    handleSubmit,
    // formState: { isValid },
    watch,
    setValue,
  } = useForm<TSchema>({
    resolver: zodResolver(Schema),
    defaultValues: {
      typeOfCare: formState.typeOfCare ?? undefined,
      reasonForHospitalization: formState.reasonForHospitalization ?? undefined,
      treatmentStage: formState.treatmentStage ?? undefined,
    },
  });

  useEffect(() => {
    setIsFooterHidden(false);
  }, [openedSection]);

  const watchCategoryOfClaim = watch(EFieldNames.TYPE_OF_CARE);
  const watchTreatmentStage = watch(EFieldNames.TREATMENT_STAGE);
  const watchReason = watch('reasonForHospitalization');
  const claimCategoryTriggerRef = useRef<HTMLButtonElement>(null);
  const treatmentDetailsTriggerRef = useRef<HTMLButtonElement>(null);
  const treatmentStageTriggerRef = useRef<HTMLButtonElement>(null);
  const currentPatientDetails = useClaimCreateStore(
    (state) => state.currentPatientDetails
  );

  const selectedStage = currentFeatureCategory?.treatmentStageOptionList?.find(
    (stage) => {
      return stage.slug === watchTreatmentStage;
    }
  );

  const onSubmit: SubmitHandler<TSchema> = (data) => {
    setFormState({ ...data });
    setCurrentStep(EClaimCreateFlowSteps.COLLECT_MORE_INFO);
  };

  const setCurrentFeatureCategory = useClaimCreateStore(
    (state) => state.setCurrentFeatureCategory
  );

  const layoutProps = {
    headerProps: {
      onBack: () => setCurrentStep(EClaimCreateFlowSteps.SELECT_PATIENT),
      title: 'Tell us about your claim',
      description: 'We need this information to better understand your case',
      children: (
        <div className="flex justify-between md:hidden">
          <h4 className="text-sm text-mobile-hero-100 font-medium leading-5">
            Cashless Claim
          </h4>
          <span className="text-sm leading-5 text-mobile-text-hierarchy-2">
            Step 2/4
          </span>
        </div>
      ),
    },
  };

  if (
    (Object.keys(currentPatientDetails?.featureCategories ?? {})?.length ??
      0) === 0
  ) {
    return (
      <ErrorScreen
        layoutProps={{
          headerProps: {
            onBack: layoutProps?.headerProps?.onBack,
          },
        }}
        heading="You can file a cashless claim only using your health insurance"
        description={null}
        actions={[
          <Button
            intent="secondary"
            className="border-primary-500 border"
            onClick={() =>
              setCurrentStep(EClaimCreateFlowSteps.SELECT_CLAIM_TYPE)
            }
          >
            Go back
          </Button>,
        ]}
      />
    );
  }

  return (
    <Layout.Main {...layoutProps}>
      {({ getDimensions }) => {
        const onError: SubmitErrorHandler<TSchema> = () => {
          toast.error('Please fill all the mandatory fields', {
            id: 'create-claim-error',
            style: {
              marginBottom: getDimensions('claims-footer').height,
            },
          });
        };
        return (
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-1 flex-col h-full pb-24"
          >
            <Accordion
              collapsible
              value={openedSection}
              onValueChange={setOpenedSection}
              type="single"
              className="flex flex-1 h-full flex-col gap-5 w-full"
            >
              {/* typeOfCare */}
              <AccordionItem
                className={clsx({
                  'w-full rounded-2xl bg-white border p-5 md:p-6 border-mobile-gray-100 transition-shadow duration-200':
                    true,
                  'border-mobile-gray-300 shadow-sm':
                    openedSection === EFieldNames.TYPE_OF_CLAIM,
                })}
                value="typeOfClaim"
              >
                <AccordionTrigger
                  ref={claimCategoryTriggerRef}
                  className="w-full"
                >
                  <div className="flex justify-between items-center">
                    <div
                      className={clsx({
                        'flex flex-col gap-2': true,
                        '!gap-1': openedSection === EFieldNames.TYPE_OF_CLAIM,
                      })}
                    >
                      {openedSection === EFieldNames.TYPE_OF_CLAIM ? (
                        <p className="text-start font-chromatica font-black leading-[140%] text-xl md:text-2xl text-mobile-text-main">
                          What's the category of your claim?
                        </p>
                      ) : (
                        <p className="text-mobile-text-hierarchy-2 text-start text-sm md:text-base font-medium leading-[160%]">
                          Claim category
                        </p>
                      )}
                      {openedSection === EFieldNames.TYPE_OF_CLAIM ? (
                        <p className="text-start text-mobile-text-hierarchy-2 md:text-base text-base leading-[140%]">
                          Select a category basis the length of the
                          hospitalisation or type of expense
                        </p>
                      ) : (
                        <p className="text-mobile-text-main font-semibold md:text-xl leading-[160%] text-left">
                          {CARE_TYPE_CONFIG.get(watchCategoryOfClaim)?.title ??
                            'Select a category'}
                        </p>
                      )}
                    </div>
                    {openedSection === EFieldNames.TYPE_OF_CLAIM ? (
                      <ChevronUp
                        className="flex-shrink-0"
                        height={16}
                        width={16}
                      />
                    ) : (
                      <ChevronDown
                        className="flex-shrink-0"
                        height={16}
                        width={16}
                      />
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="mt-6 md:mt-8">
                  <ClaimCategoryInput
                    onOptionSelect={(category) => {
                      setCurrentFeatureCategory(category ?? null);
                      setOpenedSection(EFieldNames.TREATMENT_DETAILS);
                      treatmentDetailsTriggerRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        inline: 'end',
                      });
                    }}
                    // @ts-ignore
                    register={register}
                    // @ts-ignore
                    setValue={setValue}
                    selectedValue={watchCategoryOfClaim}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* hospitalizationReason */}
              <AccordionItem
                className={clsx({
                  'w-full rounded-2xl bg-white border p-5 md:p-6 border-mobile-gray-100 transition-shadow duration-200':
                    true,
                  'border-mobile-gray-300 shadow-sm':
                    openedSection === EFieldNames.TREATMENT_DETAILS,
                })}
                value="treatmentDetails"
              >
                <AccordionTrigger
                  ref={treatmentDetailsTriggerRef}
                  className="w-full"
                >
                  <div className="flex justify-between items-center">
                    <div
                      className={clsx({
                        'flex flex-col gap-2': true,
                        '!gap-1':
                          openedSection === EFieldNames.TREATMENT_DETAILS,
                      })}
                    >
                      {openedSection === EFieldNames.TREATMENT_DETAILS ? (
                        <p className="text-start font-chromatica font-black leading-[140%] text-xl md:text-2xl text-mobile-text-main">
                          What is the name of the treatment
                        </p>
                      ) : (
                        <p className="text-mobile-text-hierarchy-2 text-start text-sm md:text-base font-medium leading-[160%]">
                          Treatment details
                        </p>
                      )}
                      {openedSection ===
                      EFieldNames.TREATMENT_DETAILS ? null : (
                        // <p className="text-start text-mobile-text-hierarchy-2 md:text-base text-base leading-[140%]">
                        //   Let us know what the reason for your treatment
                        // </p>
                        <p className="text-mobile-text-main font-semibold md:text-xl leading-[160%] text-left">
                          {watchReason?.length > 0
                            ? watchReason
                            : 'Describe your treatment'}
                        </p>
                      )}
                    </div>
                    {openedSection === EFieldNames.TREATMENT_DETAILS ? (
                      <ChevronUp
                        className="flex-shrink-0"
                        height={16}
                        width={16}
                      />
                    ) : (
                      <ChevronDown
                        className="flex-shrink-0"
                        height={16}
                        width={16}
                      />
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="mt-6 md:mt-8">
                  <TreatmentDetailsInput
                    ref={(node) => {
                      setInputRef(node);
                      register('reasonForHospitalization').ref(node);
                    }}
                    onBlur={() => {
                      if (isWebView) {
                        setIsFooterHidden(false);
                      }
                    }}
                    onFocus={() => {
                      if (isWebView) {
                        setIsFooterHidden(true);
                      }
                    }}
                    onDetailsSubmit={() => {
                      setOpenedSection(EFieldNames.TREATMENT_STAGE);
                      treatmentStageTriggerRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        inline: 'end',
                      });
                    }}
                    // @ts-ignore
                    register={register}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* claim stage */}
              <AccordionItem
                className={clsx({
                  'w-full rounded-2xl bg-white border p-5 md:p-6 border-mobile-gray-100 transition-shadow duration-200':
                    true,
                  'border-mobile-gray-300 shadow-sm':
                    openedSection === EFieldNames.TREATMENT_STAGE,
                })}
                value="treatmentStage"
              >
                <AccordionTrigger
                  ref={treatmentStageTriggerRef}
                  className="w-full"
                >
                  <div className="flex justify-between items-center">
                    <div
                      className={clsx({
                        'flex flex-col gap-2': true,
                        '!gap-1': openedSection === EFieldNames.TREATMENT_STAGE,
                      })}
                    >
                      {openedSection === EFieldNames.TREATMENT_STAGE ? (
                        <p className="text-start font-chromatica font-black leading-[140%] text-xl md:text-2xl text-mobile-text-main">
                          What stage of the treatment are you at?
                        </p>
                      ) : (
                        <p className="text-mobile-text-hierarchy-2 text-start text-sm md:text-base font-medium leading-[160%]">
                          Treatment stage
                        </p>
                      )}
                      {openedSection === EFieldNames.TREATMENT_STAGE ? (
                        <p className="text-start text-mobile-text-hierarchy-2 md:text-base text-base leading-[140%]">
                          This helps us better understand how we can guide you
                        </p>
                      ) : (
                        <p className="text-mobile-text-main font-semibold md:text-xl leading-[160%] text-left">
                          {selectedStage?.title ??
                            'Select hopitalisation status'}
                        </p>
                      )}
                    </div>
                    {openedSection === EFieldNames.TREATMENT_STAGE ? (
                      <ChevronUp
                        className="flex-shrink-0"
                        height={16}
                        width={16}
                      />
                    ) : (
                      <ChevronDown
                        className="flex-shrink-0"
                        height={16}
                        width={16}
                      />
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="mt-6 md:mt-8">
                  {currentFeatureCategory == null ? (
                    <div className="px-3 py-4 border border-gray-400 rounded-2xl bg-mobile-gray-50 flex items-center gap-2">
                      {/* icon */}
                      <InfoIcon
                        className="w-5 h-5"
                        fill={get(twConfig, 'theme.colors.gray[500]')}
                      />
                      <p className="text-gray-600 text-base font-semibold leading-[137.5%]">
                        Please pick your category of claim before the stage
                      </p>
                    </div>
                  ) : (
                    <TreatmentStateInput
                      onOptionSelect={() => {
                        setOpenedSection('');
                      }}
                      selectedValue={watchTreatmentStage}
                      // @ts-ignore
                      register={register}
                    />
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            {isFooterHidden ? null : (
              <Layout.Footer isSticky containerClassName="flex justify-between">
                <Stepper
                  className="hidden md:flex"
                  activeStep={2}
                  activeSubStep={0}
                  totalSubSteps={0}
                  totalSteps={4}
                  title="Basic details"
                />
                <Button
                  type="submit"
                  className="ml-auto md:max-w-[260px] w-full text-base"
                >
                  Continue
                </Button>
              </Layout.Footer>
            )}
          </form>
        );
      }}
    </Layout.Main>
  );
};

export default BasicInformation;
